import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { useAnswerReport } from "@/profitability/report-query";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";

export const BuildQuestions = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const answer = useAnswerReport(businessId);
  const [answers, setAnswers] = useState<string[]>(() =>
    report.build_questions.map(() => ""),
  );
  const complete = answers.every((text) => text.trim().length > 0);

  return (
    <Card
      className="flex flex-col gap-6 border-line p-6 shadow-none"
      data-testid="build-questions"
    >
      <Stack align="start" gap={2}>
        <Text render={<h2 />} variant="heading">
          Volca needs a few answers to build this agent
        </Text>
        {report.build_notes && (
          <Text tone="muted" className="whitespace-pre-line">
            {report.build_notes}
          </Text>
        )}
      </Stack>
      {report.build_questions.map((question, index) => (
        <Stack className="w-full" align="start" key={question} gap={2}>
          <Label htmlFor={`build-answer-${index}`}>{question}</Label>
          <Textarea
            id={`build-answer-${index}`}
            rows={2}
            value={answers[index] ?? ""}
            onChange={(event) =>
              setAnswers((current) =>
                current.map((text, at) =>
                  at === index ? event.target.value : text,
                ),
              )
            }
          />
        </Stack>
      ))}
      <Stack direction="row" gap={2} align="center" className="flex-wrap">
        <Button
          disabled={!complete || answer.isPending}
          aria-busy={answer.isPending}
          onClick={() =>
            answer.mutate(
              { reportId: report.id, answers: answers.map((a) => a.trim()) },
              {
                onSuccess: () =>
                  toast.success("Volca is building the agent", {
                    description:
                      "Your answers are added to the description. This usually takes a few minutes.",
                  }),
                onError: (error) =>
                  toast.error("The answers could not be sent", {
                    description: reportError(error),
                  }),
              },
            )
          }
        >
          Send answers
        </Button>
        <Text tone="muted">
          Answer every question. Your answers are added to the description.
        </Text>
      </Stack>
    </Card>
  );
};
